import './styles/main.css';

import type { ComponentProps, ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import { App } from '~/App';
import { BrandProvider } from '~/modules/branding/components/BrandProvider';
import { ThemeProvider } from '~/modules/branding/components/ThemeProvider';
import type { BrandType } from '~/modules/branding/types';
import { RemotesProvider } from '~/modules/remotes/components/RemotesProvider';

export interface ShellProps {
  localBrand?: BrandType;
  /** When omitted, defaults to `window.location.hostname` in the browser, or `""` elsewhere. */
  hostname?: string;
  overrides?: ComponentProps<typeof RemotesProvider>['overrides'];
  /**
   * When defined (including `null`), the full container {@link App} is not mounted.
   * Omit `children` to render the default `App` inside branding + remotes.
   */
  children?: ReactNode;
}

function Shell({
  localBrand,
  hostname: hostnameProp,
  overrides,
  children,
}: ShellProps) {
  let hostname = '';

  if (hostnameProp !== undefined) {
    hostname = hostnameProp;
  } else if (typeof window !== 'undefined') {
    hostname = window.location.hostname;
  }

  return (
    <RemotesProvider overrides={overrides}>
      <HelmetProvider>
        <BrandProvider localBrand={localBrand} hostname={hostname}>
          <ThemeProvider>
            {children !== undefined ? children : <App />}
          </ThemeProvider>
        </BrandProvider>
      </HelmetProvider>
    </RemotesProvider>
  );
}

Shell.displayName = 'Shell';

// eslint-disable-next-line import/no-default-export
export default Shell;
